import { apiClient } from '@/lib/api';
import { extractTenantSubdomain } from '@/lib/tenant/tenant-host';
import type { User, UserStatus } from '@/types';
import type { KYBStatus } from '@/stores/auth.store';

export interface LoginPayload {
  email: string;
  password: string;
}

/** Raw user shape as returned by the auth endpoints — mapped to the app's `User` type via `mapBackendUser`. */
export interface RawAuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: string;
  roles?: { name: string }[];
  status: UserStatus;
  profilePicture?: string | null;
  phoneNumber?: string | null;
  tenant?: { id: string; name: string };
  assignedSiteId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  permissions?: string[];
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  user: RawAuthUser;
  kybStatus?: KYBStatus;
  mustChangePassword?: boolean;
  mustCompleteMineSetup?: boolean;
  mustCompleteInspectorProfile?: boolean;
}

const AUTH_BASE = '/auth';

/** The API has its own hostname, so the backend cannot infer the workspace from it. */
function currentWorkspace(): string | undefined {
  return extractTenantSubdomain() ?? undefined;
}

export const authService = {
  async login(payload: LoginPayload): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>(
      `${AUTH_BASE}/login`,
      { ...payload, workspace: currentWorkspace() },
    );
    return data;
  },

  async getProfile(): Promise<User> {
    const { data } = await apiClient.get<User>(`${AUTH_BASE}/get-profile`);
    return data;
  },

  /**
   * Revoke the current session server-side so the just-used access + refresh
   * tokens can no longer be replayed. Best-effort: the caller still clears
   * local state regardless of the outcome.
   *
   * The token is passed explicitly (rather than relying on the request
   * interceptor) so the caller can clear the auth store synchronously without a
   * race — the interceptor only sets the header when a token is present in the
   * store, so an explicit header survives a just-cleared store.
   */
  async logout(token: string): Promise<void> {
    await apiClient.post(`${AUTH_BASE}/logout`, undefined, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 5_000,
      // A 401 here just means the token already expired — nothing to recover.
      redirectOnError: false,
    });
  },

  async forgotPassword(email: string): Promise<void> {
    await apiClient.post(`${AUTH_BASE}/forgot-password`, {
      email,
      workspace: currentWorkspace(),
    });
  },

  async resetPassword(email: string, otp: string, newPassword: string): Promise<void> {
    await apiClient.patch(`${AUTH_BASE}/reset-password`, {
      email,
      otp: Number(otp),
      newPassword,
      workspace: currentWorkspace(),
    });
  },
};